import React from 'react';
import {
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  FormControl,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Select,
  Skeleton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Plus,
  Search,
  Trash2,
} from 'lucide-react';
import { Drawer } from '../../../../../../../ui/components/Common/Drawer/Drawer';
import { CCAConfig } from '../../../../../../../utils/types/cca_types';
import {
  CCASubdomain,
  CCA_SUBDOMAINS,
} from '../../../../../../questions/CCABank/ccaConstants';
import { useManualSelection } from '../hooks/useManualSelection';
import { getSubdomainColor, toTitleCase } from '../utils/ccaHelpers';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  jobOpeningId: string;
  existingConfig?: CCAConfig;
}

export const ManualSelectionDrawer: React.FC<Props> = ({
  isOpen,
  onClose,
  jobOpeningId,
  existingConfig,
}) => {
  const {
    searchText,
    setSearchText,
    filterSubdomain,
    setFilterSubdomain,
    filterLevel,
    setFilterLevel,
    filterType,
    setFilterType,
    page,
    setPage,
    selected,
    expandedIds,
    isSaving,
    isPreloading,
    isListLoading,
    visibleQuestions,
    total,
    totalPages,
    resetPage,
    toggleExpand,
    addQuestion,
    removeQuestion,
    clearSelected,
    handleSave,
  } = useManualSelection({ jobOpeningId, existingConfig });

  const footer =
    selected.length > 0 ? (
      <Button
        type='button'
        variant='contained'
        color='success'
        fullWidth
        disabled={isSaving}
        onClick={handleSave}
        startIcon={isSaving ? <CircularProgress size={16} color='inherit' /> : undefined}
        sx={{ textTransform: 'none', fontWeight: 600, py: 1.2, borderRadius: 2 }}
      >
        {isSaving
          ? 'Saving…'
          : `Save ${selected.length} Question${selected.length !== 1 ? 's' : ''}`}
      </Button>
    ) : undefined;

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      title='Manual Selection'
      footerContent={footer}
      width='w-full md:w-2/3'
    >
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2 }}>
        {/* ── Left: Browse panel ── */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {/* Filters */}
          <Box sx={{ mb: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            <TextField
              size='small'
              placeholder='Search questions…'
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position='start'>
                    <Search size={16} />
                  </InputAdornment>
                ),
                sx: { borderRadius: 2, fontSize: '0.8rem' },
              }}
              sx={{ minWidth: 160, flex: 1 }}
            />

            <FormControl size='small' sx={{ minWidth: 130 }}>
              <Select
                value={filterSubdomain}
                onChange={(e) => {
                  setFilterSubdomain(e.target.value as CCASubdomain | 'All');
                  resetPage();
                }}
                sx={{ borderRadius: 2, fontSize: '0.8rem' }}
              >
                <MenuItem value='All'>All Subdomains</MenuItem>
                {CCA_SUBDOMAINS.map((sd) => (
                  <MenuItem key={sd} value={sd}>
                    {sd}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size='small' sx={{ minWidth: 100 }}>
              <Select
                value={filterLevel}
                onChange={(e) => {
                  setFilterLevel(e.target.value === 'All' ? 'All' : Number(e.target.value));
                  resetPage();
                }}
                sx={{ borderRadius: 2, fontSize: '0.8rem' }}
              >
                <MenuItem value='All'>All Levels</MenuItem>
                {Array.from({ length: 10 }, (_, i) => i + 1).map((l) => (
                  <MenuItem key={l} value={l}>
                    Level {l}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size='small' sx={{ minWidth: 90 }}>
              <Select
                value={filterType}
                onChange={(e) => {
                  setFilterType(e.target.value);
                  resetPage();
                }}
                sx={{ borderRadius: 2, fontSize: '0.8rem' }}
              >
                <MenuItem value='All'>All Types</MenuItem>
                <MenuItem value='multiple_choice'>MC</MenuItem>
                <MenuItem value='short_answer'>SA</MenuItem>
              </Select>
            </FormControl>
          </Box>

          {/* Question list */}
          <Box sx={{ pr: 0.5 }}>
            {isListLoading ? (
              <Stack spacing={1}>
                {Array.from({ length: 8 }).map((_, i) => (
                  <Skeleton key={i} variant='rounded' height={48} sx={{ borderRadius: 2 }} />
                ))}
              </Stack>
            ) : visibleQuestions.length === 0 ? (
              <Box sx={{ py: 6, textAlign: 'center' }}>
                <Typography variant='body2' sx={{ color: 'text.secondary' }}>
                  No questions match filters
                </Typography>
              </Box>
            ) : (
              <Stack spacing={1}>
                {visibleQuestions.map((q) => {
                  const isExpanded = expandedIds.has(q.id);
                  return (
                    <Paper
                      key={q.id}
                      variant='outlined'
                      sx={{ borderRadius: 2, overflow: 'hidden' }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1.25 }}>
                        <IconButton
                          size='small'
                          onClick={() => toggleExpand(q.id)}
                          sx={{
                            transform: isExpanded ? 'rotate(180deg)' : 'none',
                            transition: 'transform 0.15s',
                          }}
                        >
                          <ChevronDown size={14} />
                        </IconButton>
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                          <Typography variant='caption' noWrap sx={{ display: 'block', fontWeight: 600, color: 'text.primary' }}>
                            {q.question.text}
                          </Typography>
                          <Box sx={{ mt: 0.5, display: 'flex', gap: 0.75 }}>
                            <Chip
                              label={toTitleCase(q.subdomain).split(' ')[0]}
                              size='small'
                              className={getSubdomainColor(q.subdomain)}
                              sx={{ fontSize: '0.65rem', height: 18, fontWeight: 700 }}
                            />
                            <Chip
                              label={`Lvl ${q.level}`}
                              size='small'
                              sx={{ fontSize: '0.65rem', height: 18 }}
                            />
                            <Chip
                              label={q.questionType === 'multiple_choice' ? 'MC' : 'SA'}
                              size='small'
                              color='info'
                              variant='outlined'
                              sx={{ fontSize: '0.65rem', height: 18, fontWeight: 700 }}
                            />
                          </Box>
                        </Box>
                        <Button
                          size='small'
                          variant='contained'
                          color='success'
                          onClick={() => addQuestion(q)}
                          startIcon={<Plus size={12} />}
                          sx={{
                            textTransform: 'none',
                            fontSize: '0.75rem',
                            fontWeight: 600,
                            borderRadius: 1.5,
                            px: 1.2,
                            py: 0.4,
                            minWidth: 'unset',
                          }}
                        >
                          Add
                        </Button>
                      </Box>
                      <Collapse in={isExpanded}>
                        <Box sx={{ borderTop: '1px solid', borderColor: 'divider', px: 2, py: 1.25, bgcolor: 'action.hover' }}>
                          <Typography variant='caption' sx={{ fontWeight: 700, color: 'text.secondary' }}>
                            Ideal Answer:
                          </Typography>
                          <Typography variant='caption' sx={{ display: 'block', mt: 0.5, color: 'text.primary' }}>
                            {q.idealAnswer}
                          </Typography>
                        </Box>
                      </Collapse>
                    </Paper>
                  );
                })}
              </Stack>
            )}
          </Box>

          {/* Pagination */}
          {!isListLoading && totalPages > 1 && (
            <Box sx={{ mt: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderTop: '1px solid', borderColor: 'divider', pt: 1.5 }}>
              <Typography variant='caption' sx={{ color: 'text.secondary' }}>
                Page {page} of {totalPages} ({total.toLocaleString()} total)
              </Typography>
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                <IconButton
                  size='small'
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1.5 }}
                >
                  <ChevronLeft size={14} />
                </IconButton>
                <IconButton
                  size='small'
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1.5 }}
                >
                  <ChevronRight size={14} />
                </IconButton>
              </Box>
            </Box>
          )}
        </Box>

        {/* ── Right: Selected panel ── */}
        <Box sx={{ width: { xs: '100%', md: 240 }, flexShrink: 0 }}>
          <Box sx={{ mb: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant='caption' sx={{ fontWeight: 700, color: 'text.primary' }}>
              Selected ({selected.length})
            </Typography>
            {selected.length > 0 && (
              <Button
                size='small'
                color='error'
                onClick={clearSelected}
                sx={{ textTransform: 'none', fontSize: '0.75rem', fontWeight: 600, minWidth: 'unset', p: 0 }}
              >
                Clear all
              </Button>
            )}
          </Box>

          {selected.length === 0 ? (
            <Paper
              variant='outlined'
              sx={{
                display: 'flex',
                minHeight: 200,
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: 2,
                borderStyle: 'dashed',
                borderColor: 'divider',
                p: 2,
                color: 'text.secondary',
              }}
            >
              {isPreloading ? (
                <>
                  <CircularProgress size={16} sx={{ mb: 1 }} />
                  <Typography variant='caption' sx={{ color: 'text.secondary' }}>
                    Loading saved…
                  </Typography>
                </>
              ) : (
                <Typography variant='caption' sx={{ color: 'text.secondary' }}>
                  No questions selected
                </Typography>
              )}
            </Paper>
          ) : (
            <Stack spacing={1}>
              {selected.map((q, i) => (
                <Paper
                  key={q.id}
                  variant='outlined'
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 1,
                    borderRadius: 1.5,
                    p: 1,
                  }}
                >
                  <Avatar
                    sx={{
                      width: 18,
                      height: 18,
                      fontSize: '0.65rem',
                      fontWeight: 700,
                      bgcolor: 'success.light',
                      color: 'success.dark',
                      mt: 0.25,
                    }}
                  >
                    {i + 1}
                  </Avatar>
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography
                      variant='caption'
                      sx={{
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        fontWeight: 500,
                        lineHeight: 1.3,
                      }}
                    >
                      {q.question.text}
                    </Typography>
                    <Chip
                      label={toTitleCase(q.subdomain).split(' ')[0]}
                      size='small'
                      className={getSubdomainColor(q.subdomain)}
                      sx={{ mt: 0.5, fontSize: '0.6rem', height: 16, fontWeight: 700 }}
                    />
                  </Box>
                  <IconButton
                    size='small'
                    onClick={() => removeQuestion(q.id)}
                    sx={{ color: 'error.main', p: 0.5 }}
                  >
                    <Trash2 size={12} />
                  </IconButton>
                </Paper>
              ))}
            </Stack>
          )}
        </Box>
      </Box>
    </Drawer>
  );
};

export default ManualSelectionDrawer;
